import React, { useState, useEffect, useRef } from 'react';
import { updateOrder, patchOrders, logError } from '../../api';
import { isPropietarioOrAdminOrGestion, isJefeTransportistas } from '../../services/AuthService';

// Cache global para evitar cargas múltiples
const optionsCache = {};
const loadingPromises = {};

const GenericAssigner = ({
  order,
  onChange,
  multiSelect = false,
  selectedOrderIds = [],
  preChargedOptions,
  fetchOptions,
  fieldName,
  displayField = 'name',
  valueField = 'valueId'
}) => {
  const [options, setOptions] = useState([]);
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [selectedValue, setSelectedValue] = useState(
    order[fieldName] != null ? String(order[fieldName]) : (fieldName === 'carrierId' ? '0' : '')
  );
  const [currentOption, setCurrentOption] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const selectRef = useRef(null);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        let opts = preChargedOptions;
        if (!opts) {
          // Verificar cache primero
          if (optionsCache[fieldName]) {
            opts = optionsCache[fieldName];
          } else {
            // Si ya hay una promesa de carga en curso, esperarla
            if (loadingPromises[fieldName]) {
              opts = await loadingPromises[fieldName];
            } else {
              // Crear nueva promesa de carga
              loadingPromises[fieldName] = fetchOptions();
              opts = await loadingPromises[fieldName];
              optionsCache[fieldName] = opts;
              delete loadingPromises[fieldName];
            }
          }
        }
        setOptions(opts);
        setOptionsLoaded(true);
      } catch (error) {
        delete loadingPromises[fieldName];
        logError(error);
        console.error(`Error al obtener ${fieldName}:`, error);
      }
    };

    if (!optionsLoaded) {
      loadOptions();
    }

    if ((isPropietarioOrAdminOrGestion() || isJefeTransportistas()) && !order.isFinalStatus) {
      setCanEdit(true);
    }
  }, [order.isFinalStatus, optionsLoaded, fieldName, fetchOptions, preChargedOptions]);

  useEffect(() => {
    if (optionsLoaded) {
      const match = options.find(opt => opt[valueField] === order[fieldName]);
      if (match) {
        setCurrentOption(match);
        setSelectedValue(String(match[valueField]));
      } else {
        setCurrentOption(null);
        setSelectedValue(fieldName === 'carrierId' ? '0' : '');
      }
    }
  }, [optionsLoaded, options, order[fieldName], valueField, fieldName]);

  // Actualizar cuando cambie el objeto carrier/timeSlot/etc del order
  useEffect(() => {
    if (fieldName === 'carrierId' && order.carrier) {
      setCurrentOption(order.carrier);
    } else if (fieldName === 'timeSlotId' && order.timeSlot) {
      setCurrentOption(order.timeSlot);
    } else if (fieldName === 'clientOriginId' && order.clientOrigin) {
      setCurrentOption(order.clientOrigin);
    } else if (fieldName === 'paymentMethodId' && order.paymentMethod) {
      setCurrentOption(order.paymentMethod);
    }
  }, [order.carrier, order.timeSlot, order.clientOrigin, order.paymentMethod, fieldName]);

  const handleClickOutside = (event) => {
    if (multiSelect) return;
    if (selectRef.current && !selectRef.current.contains(event.target)) {
      setIsEditing(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [multiSelect]);

  const handleEditClick = () => {
    if (canEdit) {
      setIsEditing(true);
    }
  };

  const handleValueChange = async (e) => {
    const valueStr = e.target.value;
    setSelectedValue(valueStr);

    const newValue = (valueStr === '' || valueStr === '0') ? 0 : Number(valueStr);

    if (multiSelect && Array.isArray(selectedOrderIds) && selectedOrderIds.length > 0) {
      try {
        await patchOrders(selectedOrderIds, { [fieldName]: newValue });
        const newOption = options.find(opt => opt[valueField] === newValue) || null;
        onChange(selectedOrderIds, newOption);
      } catch (err) {
        logError(err);
        console.error(`Error al actualizar ${fieldName} de varios pedidos:`, err);
      }
    }

    if (!multiSelect) {
      try {
        await updateOrder(order.id, { [fieldName]: newValue, id: order.id });
        const newOption = options.find(opt => opt[valueField] === newValue) || null;
        setCurrentOption(newOption);
        onChange(order.id, newOption);
        setIsEditing(false);
      } catch (err) {
        logError(err);
        console.error(`Error al actualizar ${fieldName} del pedido:`, err);
      }
    }
  };

  const getDisplayValue = () => {
    if (fieldName === 'timeSlotId' && currentOption) {
      return `${currentOption.name} ${currentOption.val1} a ${currentOption.val2}`;
    }
    if (fieldName === 'carrierId' && currentOption) {
      return `${currentOption.firstName} ${currentOption.lastName1}`;
    }
    return currentOption ? currentOption[displayField] : (fieldName === 'carrierId' ? 'Sin Asignar' : 'Sin especificar');
  };

  const getDisplayStyle = () => {
    if (fieldName === 'carrierId' && currentOption?.colourHex) {
      return {
        color: canEdit ? '#007bff' : 'inherit',
        cursor: canEdit ? 'pointer' : 'default',
        textDecoration: canEdit ? 'underline' : 'none',
        backgroundColor: currentOption.colourHex,
      };
    }
    return {
      color: canEdit ? '#007bff' : 'inherit',
      cursor: canEdit ? 'pointer' : 'default',
      textDecoration: canEdit ? 'underline' : 'none',
    };
  };

  const getTextColor = () => {
    if (fieldName === 'carrierId' && currentOption?.colourHex) {
      const { getContrastTextColor } = require('../../utils/ColorUtils');
      return getContrastTextColor(currentOption.colourHex);
    }
    return canEdit ? '#007bff' : 'inherit';
  };

  return (
    <span>
      {(!isEditing && !multiSelect) ? (
        <span
          onClick={handleEditClick}
          style={getDisplayStyle()}
        >
          <span style={{ color: getTextColor() }}>
            {getDisplayValue()}
          </span>
        </span>
      ) : (
        <select
          ref={selectRef}
          value={selectedValue}
          onChange={handleValueChange}
          className="form-select"
          autoFocus
        >
          {fieldName === 'carrierId' ? (
            <option value="0">Sin Asignar</option>
          ) : (
            <option value="">Sin Especificar</option>
          )}
          {options.map((option) => (
            <option key={option[valueField]} value={option[valueField]}>
              {fieldName === 'carrierId' 
                ? `${option.firstName} ${option.lastName1}`
                : option[displayField]
              }
            </option>
          ))}
        </select>
      )}
    </span>
  );
};

export default GenericAssigner;